import { computed } from 'vue'
import { useState } from '#app'
import {
  OFF_MAINTENANCE_RESPONSE,
  type FeatureKey,
  type MaintenanceScopeState,
  type MaintenanceStatusResponse,
} from '~~/shared/maintenance/contracts'

interface MaintenanceStatusState {
  response: MaintenanceStatusResponse
  hasLoaded: boolean
}

const MAINTENANCE_STATUS_STATE_KEY = 'maintenance-status'
const SECONDS_UNTIL_NEXT_POLL_STATE_KEY = 'maintenance-status-countdown'

let pollTimer: ReturnType<typeof setInterval> | null = null

function buildDefaultState(): MaintenanceStatusState {
  return {
    response: OFF_MAINTENANCE_RESPONSE,
    hasLoaded: false,
  }
}

export function useMaintenanceStatus() {
  const state = useState<MaintenanceStatusState>(
    MAINTENANCE_STATUS_STATE_KEY,
    () => buildDefaultState()
  )
  const secondsUntilNextPoll = useState<number>(
    SECONDS_UNTIL_NEXT_POLL_STATE_KEY,
    () => 0
  )

  const refreshMaintenanceStatus = async () => {
    try {
      const response = await $fetch<MaintenanceStatusResponse>(
        '/api/maintenance/status',
        { method: 'GET', ignoreErrorToast: true }
      )
      state.value = { response, hasLoaded: true }
    } catch {
      // Fail open: keep the last-known state (or the "off" default) rather
      // than blocking the app because the status endpoint is unreachable.
    }
  }

  const startPolling = async () => {
    if (pollTimer) {
      return
    }

    await refreshMaintenanceStatus()
    const intervalSeconds = Math.max(
      1,
      state.value.response.pollIntervalSeconds
    )
    secondsUntilNextPoll.value = intervalSeconds

    // A single 1s tick both drives the visible countdown and triggers the
    // actual refetch once it reaches zero, so the two can never drift apart.
    pollTimer = setInterval(async () => {
      secondsUntilNextPoll.value = Math.max(0, secondsUntilNextPoll.value - 1)
      if (secondsUntilNextPoll.value === 0) {
        await refreshMaintenanceStatus()
        secondsUntilNextPoll.value = intervalSeconds
      }
    }, 1000)
  }

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  const featureState = (feature: FeatureKey): MaintenanceScopeState =>
    state.value.response.features[feature] || { status: 'off' }

  return {
    appStatus: computed(() => state.value.response.app.status),
    appMessage: computed(() => state.value.response.app.message),
    appEta: computed(() => state.value.response.app.eta),
    isAppDegraded: computed(
      () => state.value.response.app.status === 'degraded'
    ),
    isAppDisabled: computed(
      () => state.value.response.app.status === 'disabled'
    ),
    featureStatus: (feature: FeatureKey) => featureState(feature).status,
    featureMessage: (feature: FeatureKey) => featureState(feature).message,
    featureEta: (feature: FeatureKey) => featureState(feature).eta,
    isFeatureDegraded: (feature: FeatureKey) =>
      featureState(feature).status === 'degraded',
    isFeatureDisabled: (feature: FeatureKey) =>
      featureState(feature).status === 'disabled',
    secondsUntilNextPoll: computed(() => secondsUntilNextPoll.value),
    refreshMaintenanceStatus,
    startPolling,
    stopPolling,
  }
}
